import type { ArmorCategory } from '@character-creator/types';

export interface Armor {
  id: string;
  name: string;
  category: ArmorCategory;
  baseAC: number;
  addDexModifier: boolean;
  maxDexBonus: number | null; // null = unlimited
  strengthRequirement: number | null;
  stealthDisadvantage: boolean;
  cost: { amount: number; unit: 'gp' };
  weight: number;
}

/**
 * SRD 5.1 Armor
 */
export const armor: Armor[] = [
  // Light Armor
  {
    id: 'padded',
    name: 'Padded',
    category: 'light',
    baseAC: 11,
    addDexModifier: true,
    maxDexBonus: null,
    strengthRequirement: null,
    stealthDisadvantage: true,
    cost: { amount: 5, unit: 'gp' },
    weight: 8,
  },
  {
    id: 'leather',
    name: 'Leather',
    category: 'light',
    baseAC: 11,
    addDexModifier: true,
    maxDexBonus: null,
    strengthRequirement: null,
    stealthDisadvantage: false,
    cost: { amount: 10, unit: 'gp' },
    weight: 10,
  },
  {
    id: 'studded-leather',
    name: 'Studded Leather',
    category: 'light',
    baseAC: 12,
    addDexModifier: true,
    maxDexBonus: null,
    strengthRequirement: null,
    stealthDisadvantage: false,
    cost: { amount: 45, unit: 'gp' },
    weight: 13,
  },

  // Medium Armor
  {
    id: 'hide',
    name: 'Hide',
    category: 'medium',
    baseAC: 12,
    addDexModifier: true,
    maxDexBonus: 2,
    strengthRequirement: null,
    stealthDisadvantage: false,
    cost: { amount: 10, unit: 'gp' },
    weight: 12,
  },
  {
    id: 'chain-shirt',
    name: 'Chain Shirt',
    category: 'medium',
    baseAC: 13,
    addDexModifier: true,
    maxDexBonus: 2,
    strengthRequirement: null,
    stealthDisadvantage: false,
    cost: { amount: 50, unit: 'gp' },
    weight: 20,
  },
  {
    id: 'scale-mail',
    name: 'Scale Mail',
    category: 'medium',
    baseAC: 14,
    addDexModifier: true,
    maxDexBonus: 2,
    strengthRequirement: null,
    stealthDisadvantage: true,
    cost: { amount: 50, unit: 'gp' },
    weight: 45,
  },
  {
    id: 'breastplate',
    name: 'Breastplate',
    category: 'medium',
    baseAC: 14,
    addDexModifier: true,
    maxDexBonus: 2,
    strengthRequirement: null,
    stealthDisadvantage: false,
    cost: { amount: 400, unit: 'gp' },
    weight: 20,
  },
  {
    id: 'half-plate',
    name: 'Half Plate',
    category: 'medium',
    baseAC: 15,
    addDexModifier: true,
    maxDexBonus: 2,
    strengthRequirement: null,
    stealthDisadvantage: true,
    cost: { amount: 750, unit: 'gp' },
    weight: 40,
  },

  // Heavy Armor
  {
    id: 'ring-mail',
    name: 'Ring Mail',
    category: 'heavy',
    baseAC: 14,
    addDexModifier: false,
    maxDexBonus: null,
    strengthRequirement: null,
    stealthDisadvantage: true,
    cost: { amount: 30, unit: 'gp' },
    weight: 40,
  },
  {
    id: 'chain-mail',
    name: 'Chain Mail',
    category: 'heavy',
    baseAC: 16,
    addDexModifier: false,
    maxDexBonus: null,
    strengthRequirement: 13,
    stealthDisadvantage: true,
    cost: { amount: 75, unit: 'gp' },
    weight: 55,
  },
  {
    id: 'splint',
    name: 'Splint',
    category: 'heavy',
    baseAC: 17,
    addDexModifier: false,
    maxDexBonus: null,
    strengthRequirement: 15,
    stealthDisadvantage: true,
    cost: { amount: 200, unit: 'gp' },
    weight: 60,
  },
  {
    id: 'plate',
    name: 'Plate',
    category: 'heavy',
    baseAC: 18,
    addDexModifier: false,
    maxDexBonus: null,
    strengthRequirement: 15,
    stealthDisadvantage: true,
    cost: { amount: 1500, unit: 'gp' },
    weight: 65,
  },

  // Shields
  {
    id: 'shield',
    name: 'Shield',
    category: 'shield',
    baseAC: 2, // +2 to AC
    addDexModifier: false,
    maxDexBonus: null,
    strengthRequirement: null,
    stealthDisadvantage: false,
    cost: { amount: 10, unit: 'gp' },
    weight: 6,
  },
];

/**
 * Get armor by ID
 */
export function getArmor(id: string): Armor | undefined {
  return armor.find((a) => a.id === id);
}

/**
 * Get all armor in a category
 */
export function getArmorByCategory(category: ArmorCategory): Armor[] {
  return armor.filter((a) => a.category === category);
}

/**
 * Calculate AC with armor
 */
export function calculateArmorAC(armorItem: Armor, dexModifier: number): number {
  if (!armorItem.addDexModifier) {
    return armorItem.baseAC;
  }
  const dexBonus =
    armorItem.maxDexBonus !== null
      ? Math.min(dexModifier, armorItem.maxDexBonus)
      : dexModifier;
  return armorItem.baseAC + dexBonus;
}

/**
 * Check if character meets strength requirement for armor
 */
export function meetsStrengthRequirement(
  armorItem: Armor,
  strengthScore: number
): boolean {
  if (armorItem.strengthRequirement === null) return true;
  return strengthScore >= armorItem.strengthRequirement;
}

/**
 * Format armor AC for display
 */
export function formatArmorAC(armorItem: Armor): string {
  if (armorItem.category === 'shield') {
    return `+${armorItem.baseAC}`;
  }
  if (!armorItem.addDexModifier) {
    return `${armorItem.baseAC}`;
  }
  if (armorItem.maxDexBonus !== null) {
    return `${armorItem.baseAC} + Dex (max ${armorItem.maxDexBonus})`;
  }
  return `${armorItem.baseAC} + Dex`;
}
